import { useState, useRef, useCallback } from 'react';
import { Streamdown } from 'streamdown';
import Button from '@/atoms/buttons/Button';
import IconButton from '@/atoms/buttons/IconButton';
import Size from '@enums/Size';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import { useTooltipAgent } from '@pkg/ai/client/useTooltipAgent';
import { Feature } from '@/lib/enums';
import { brand, system } from '@/lib/theme/tokens/palettes';
import useDesignStore from '@/components/DesignContainer/hooks/useDesignStore';

const Tooltip = ({ prompt }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [sample, setSample] = useState(null);
  const lastPromptRef = useRef(null);

  const access = useDesignStore((state) => state.access);
  const hasAiTooltipsFeature = access.features[Feature.AI_TOOLTIPS];

  const { completion, complete, isLoading, error } = useTooltipAgent();

  const executePrompt = useCallback(
    async (promptContent) => {
      lastPromptRef.current = promptContent;
      await complete(promptContent);
    },
    [complete]
  );

  const handleOpen = async (event) => {
    setAnchorEl(event.currentTarget);
    setSample(null);

    let promptContent;
    if (typeof prompt === 'function') {
      const result = prompt();
      if (result && typeof result === 'object' && 'text' in result) {
        promptContent = result.text;
        setSample(result.sample);
      } else {
        promptContent = result;
      }
    } else {
      promptContent = prompt;
    }

    await executePrompt(promptContent);
  };

  const handleRetry = async () => {
    if (lastPromptRef.current) {
      await executePrompt(lastPromptRef.current);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  if (!hasAiTooltipsFeature) {
    return null;
  }

  return (
    <>
      <span
        onClick={handleOpen}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <IconButton color="secondary" size={Size.MEDIUM} label="AI Explanation">
          <AutoAwesomeIcon fontSize="small" />
        </IconButton>
      </span>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box
          px={2}
          py={2}
          maxWidth={500}
          minWidth={300}
          minHeight="100px"
          sx={{
            boxShadow: `inset 4px 0 0 ${brand.yellow.main}`,
          }}
        >
          {sample && (
            <Box
              mb={1}
              px={1.5}
              py={0.5}
              sx={{
                backgroundColor: '#FFF9E6',
                borderRadius: '4px',
                fontSize: '11px',
                color: '#B08800',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                border: '1px solid #FFECB3',
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#FFB400',
                  display: 'inline-block',
                }}
              />
              Showing {sample.samplingPercentage}% sample (
              {sample.sampleSize.toLocaleString()} of{' '}
              {sample.originalSize.toLocaleString()} rows)
            </Box>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                py: 2,
              }}
            >
              <ErrorOutlineIcon
                sx={{ fontSize: 32, color: system.destructive[300] }}
              />
              <Box
                sx={{
                  fontSize: '12px',
                  color: system.destructive[400],
                  textAlign: 'center',
                }}
              >
                Failed to analyze data
              </Box>
              <Button
                size={Size.X_SMALL}
                color="secondary"
                onClick={handleRetry}
                label="Try again"
              />
            </Box>
          )}

          {/* Loading State */}
          {isLoading && !completion && !error && (
            <Box textAlign="center" p={1}>
              Loading...
            </Box>
          )}

          {/* Success State */}
          {completion && !error ? (
            <Box style={{ fontSize: '12px' }}>
              <Streamdown>{completion}</Streamdown>
            </Box>
          ) : (
            !isLoading &&
            !error && (
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  margin: 0,
                  fontFamily: 'monospace',
                  fontSize: '11px',
                }}
              >
                AI will analyze data on request...
              </pre>
            )
          )}
        </Box>
      </Popover>
    </>
  );
};

export default Tooltip;
